import React, { useEffect, useState, useCallback, useMemo, memo } from 'react';
import { useHistory } from 'react-router-dom';
import { Loader, Dropdown, Input } from 'semantic-ui-react';
import COLORS from 'constants/colors';
import maintenanceIcon from 'img/maintenance.svg';
import { Heading, Table, Filters } from 'comps';
import Pagination from 'modules/list/pagination';
import { AV_REVIEW_ROLES } from 'constants/av-review';

import { FlexView, Separator } from '@cuemath/comps';
import { Text, Button } from '@cuemath/web-comps';

import useAVReviewRole from './hooks/use-av-review-role';
import { useAdminReviews, useReviewerReviews } from './api/get-reviews';
import { useReviewStats } from './api/get-stats';
import { mapReviewsForDisplay } from './helpers';
import {
  STATUS_FILTERS,
  PER_PAGE,
  ALL_TAB_ID,
  DETECTION_TYPE_OPTIONS,
  TEACHER_ID_DEBOUNCE_MS,
} from './constants';
import ReviewRow from './components/review-row';
import StatsPanel from './components/stats-panel';
import { StateContainer, PaginationRow } from './av-review-dashboard-styled';

function AVReviewDashboard() {
  const history = useHistory();
  const [page, setPage] = useState(1);
  const [activeTab, setActiveTab] = useState(ALL_TAB_ID);
  const [teacherIdInput, setTeacherIdInput] = useState('');
  const [appliedTeacherId, setAppliedTeacherId] = useState('');
  const [reviewerFilterId, setReviewerFilterId] = useState('');
  const [detectionTypes, setDetectionTypes] = useState([]);

  const { role, userId, isLoading: isRoleLoading, reviewers } = useAVReviewRole();

  useEffect(() => {
    if (teacherIdInput === appliedTeacherId) return undefined;
    const timer = setTimeout(() => {
      setAppliedTeacherId(teacherIdInput);
      setPage(1);
    }, TEACHER_ID_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [teacherIdInput, appliedTeacherId]);

  const {
    data: adminData,
    getAll: fetchAdminReviews,
    isProcessing: isAdminProcessing,
  } = useAdminReviews();

  const {
    data: reviewerData,
    getAll: fetchReviewerReviews,
    isProcessing: isReviewerProcessing,
  } = useReviewerReviews();

  const { data: stats, get: fetchStats } = useReviewStats(userId);

  const isAdmin = role === AV_REVIEW_ROLES.ADMIN;
  const rawData = isAdmin ? adminData : reviewerData;
  const isProcessing = isAdmin ? isAdminProcessing : isReviewerProcessing;

  const pagination = rawData?.pagination;
  const displayReviews = useMemo(() => {
    const reviews = rawData?.reviews || rawData || [];

    return mapReviewsForDisplay(Array.isArray(reviews) ? reviews : []);
  }, [rawData]);

  const fetchReviews = useCallback(() => {
    const params = { page, per_page: PER_PAGE };

    if (activeTab !== ALL_TAB_ID) {
      params.status = activeTab;
    }
    if (detectionTypes.length > 0) {
      params.detection_type = detectionTypes;
    }

    if (role === AV_REVIEW_ROLES.ADMIN) {
      if (appliedTeacherId) {
        params.teacher_id = appliedTeacherId;
      }
      if (reviewerFilterId) {
        params.reviewer_id = reviewerFilterId;
      }
      fetchAdminReviews(params);
    } else if (role === AV_REVIEW_ROLES.REVIEWER && userId) {
      fetchReviewerReviews({ reviewerId: userId, ...params });
    }
  }, [
    role,
    userId,
    page,
    activeTab,
    appliedTeacherId,
    reviewerFilterId,
    detectionTypes,
    fetchAdminReviews,
    fetchReviewerReviews,
  ]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  useEffect(() => {
    if (!role) return;

    const query = role === AV_REVIEW_ROLES.ADMIN ? null : { reviewer_id: userId };

    fetchStats(userId, query);
  }, [role, userId, fetchStats]);

  const handleStatusFilter = useCallback(selected => {
    // Filters passes an array even in singleSelect mode; take the most recent pick.
    const label = Array.isArray(selected) ? selected[selected.length - 1] : selected;
    const match = STATUS_FILTERS.find(f => f.label === label);

    setActiveTab(match ? match.status : ALL_TAB_ID);
    setPage(1);
  }, []);

  const handlePageChange = useCallback(newPage => {
    setPage(newPage);
  }, []);

  const handleTeacherInputChange = useCallback((_, { value }) => {
    setTeacherIdInput(value.trim());
  }, []);

  const handleReviewerFilterChange = useCallback((_, { value }) => {
    setReviewerFilterId(value || '');
    setPage(1);
  }, []);

  const handleDetectionTypesChange = useCallback((_, { value }) => {
    setDetectionTypes(value || []);
    setPage(1);
  }, []);

  const reviewerOptions = useMemo(
    () => reviewers.map(r => ({ key: r.id, value: r.id, text: r.name || r.id })),
    [reviewers],
  );

  const detectionDropdownOptions = useMemo(
    () => DETECTION_TYPE_OPTIONS.map(o => ({ key: o.value, value: o.value, text: o.label })),
    [],
  );

  const headerCells = useMemo(() => {
    const teacherCell = {
      title: 'Teacher (ID)',
      ...(isAdmin && {
        filterApplied: Boolean(appliedTeacherId),
        showFilters: true,
        filterComponent: (
          <Input
            fluid
            placeholder="Paste teacher UUID"
            value={teacherIdInput}
            onChange={handleTeacherInputChange}
            aria-label="Filter by teacher UUID"
          />
        ),
      }),
    };
    const detectionsCell = {
      title: 'Detections',
      filterApplied: detectionTypes.length > 0,
      showFilters: true,
      filterComponent: (
        <Dropdown
          clearable
          fluid
          multiple
          onChange={handleDetectionTypesChange}
          options={detectionDropdownOptions}
          placeholder="Select detection types"
          selection
          value={detectionTypes}
          aria-label="Filter by detection types"
        />
      ),
    };
    const reviewerCell = {
      title: 'Reviewer ID',
      filterApplied: Boolean(reviewerFilterId),
      showFilters: true,
      filterComponent: (
        <Dropdown
          clearable
          fluid
          onChange={handleReviewerFilterChange}
          options={reviewerOptions}
          placeholder="Select reviewer"
          search
          selection
          value={reviewerFilterId}
          aria-label="Filter by reviewer"
        />
      ),
    };

    const base = [
      { title: 'Classroom ID' },
      teacherCell,
      { title: 'Type' },
      { title: 'Date' },
      detectionsCell,
      { title: 'Status' },
    ];
    return isAdmin ? [reviewerCell, ...base] : base;
  }, [
    isAdmin,
    appliedTeacherId,
    teacherIdInput,
    reviewerFilterId,
    detectionTypes,
    reviewerOptions,
    detectionDropdownOptions,
    handleTeacherInputChange,
    handleReviewerFilterChange,
    handleDetectionTypesChange,
  ]);

  const handleRowClick = useCallback(reviewId => {
    window.open(`/av-reviews/${reviewId}`, '_blank', 'noopener,noreferrer');
  }, []);

  const handleOpenSettings = useCallback(() => {
    history.push('/av-reviews-settings');
  }, [history]);

  if (isRoleLoading) {
    return (
      <StateContainer justifyContent="center" alignItems="center">
        <Loader inline active />
      </StateContainer>
    );
  }

  if (!role) {
    return (
      <StateContainer justifyContent="center" alignItems="center">
        <img src={maintenanceIcon} alt="No access" width={120} height={72} />
        <Separator transparent height={16} />
        <Text variant="body1Regular" color={COLORS.SILVERCHALICE}>
          You do not have access to the AV Review Dashboard.
        </Text>
      </StateContainer>
    );
  }

  return (
    <FlexView gap={16} gutter={16}>
      <FlexView flexDirection="row" justifyContent="space-between" alignItems="center">
        <Heading>AV Review Dashboard</Heading>
        {isAdmin && (
          <Button
            onClick={handleOpenSettings}
            underline
            variant="tertiaryWithUnderline"
            label="Reviewer Settings"
            underlineOffset="5px"
            size="small"
          />
        )}
      </FlexView>
      <Separator transparent height={16} />
      <StatsPanel stats={stats} />
      <Filters
        filters={STATUS_FILTERS.map(f => f.label)}
        handleFilterChange={handleStatusFilter}
        singleSelect
      />
      <FlexView>
        <Table
          headerCell={headerCells}
          dataLength={displayReviews.length}
          havePagination
          showLoader={isProcessing}
          showInfo={false}
        >
          {displayReviews.map(review => (
            <ReviewRow
              key={review.id}
              review={review}
              isAdmin={isAdmin}
              onRowClick={handleRowClick}
            />
          ))}
        </Table>
      </FlexView>
      <Separator transparent height={8} />
      <PaginationRow flexDirection="row" justifyContent="center" alignItems="center">
        {pagination?.total > 0 && (
          <Pagination
            page={page}
            perPage={PER_PAGE}
            count={pagination.total}
            onPageChange={handlePageChange}
            shouldShowNewUi
          />
        )}
      </PaginationRow>
    </FlexView>
  );
}

export default memo(AVReviewDashboard);
